import React, { Component } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from "reactstrap";
import Draggable from "react-draggable";
import moment from "moment";

import { FetchData, FetchFile } from "./utils/DataFetch";
import { GetCookie, Timeout, openFileInNewTab } from "./utils/Common";
import { TranslationHistory } from "./TranslationHistory";
import { TranslationBilling } from "./TranslationBilling";
import { ClaimantFormSearch } from "./ClaimantFormSearch";
import { ClientFormSearch } from "./ClientFormSearch";
import { RepFormSearch } from "./RepFormSearch";
import { InterpreterFormSearch } from "./InterpreterFormSearch";

import "./Translation.css";

const DATE_INPUT_FORMAT = "YYYY-MM-DD";

const fullName = (last, first) => [last, first].filter(Boolean).join(", ");

const clientLabel = (abbrv, name) => [abbrv, name].filter(Boolean).join(" - ");

const toInputDate = (value) =>
  value ? moment(value).format(DATE_INPUT_FORMAT) : "";

/**
 * Builds the form model from a grid row (camelCase from the API), or a blank
 * model for a new translation.
 */
export const toTranslationModel = (row) => ({
  EventType: row ? "update" : "insert",
  JobId: row?.jobId ?? null,
  JobNum: row?.jobNum ?? null,
  ClientId: row?.clientId ?? "",
  ClientAbbrv: row?.clientAbbrv ?? "",
  RepId: row?.repId ?? "",
  IntId: row?.intId ?? "",
  ClmId: row?.clmId ?? null,
  JobDateTime: row ? toInputDate(row.jobDateTime) : moment().format(DATE_INPUT_FORMAT),
  FinishTime: toInputDate(row?.finishTime),
  WorkFlowId: row?.workFlowId ?? "",
  IntApptNotes: row?.intApptNotes ?? "",
  InvoiceNote: row?.invoiceNote ?? "",
  IntFee: row?.intFee ?? null,
  JobTotal: row?.jobTotal ?? null,
  IntConfFlg: row?.intConfFlg ?? false,
  RepAuthorizedFlg: row?.repAuthorizedFlg ?? false,
  InvoicedFlg: row?.invoicedFlg ?? false,
  IsDeletedFlg: row?.isDeletedFlg ?? false,
  // Quote fields: printed as the "Translation Estimate and Agreement" PDF. Subject is
  // InvoiceNote; the API stores the languages in CallerNote (as JSON) and turnaround in
  // ProvNotes, but here they're plain fields.
  SourceLanguage: row?.sourceLanguage ?? "English",
  TargetLanguage: row?.targetLanguage ?? "",
  TurnaroundDays: row?.turnaroundDays ?? 5,
  // Display-only: shown for the selected client, translator, and claimant.
  ClientDisplay: row ? clientLabel(row.clientAbbrv, row.clientName) : "",
  RepDisplay: row?.repId ? fullName(row.repLastName, row.repFirstName) : "",
  TranslatorDisplay: row ? fullName(row.intLastName, row.intFirstName) : "",
  ClaimantDisplay: row?.clmId ? fullName(row.clmLastName, row.clmFirstName) : "",
});

export class TranslationForm extends Component {
  static displayName = TranslationForm.name;

  constructor(props) {
    super(props);
    this.user = JSON.parse(GetCookie("user"));
    this.state = {
      model: props.model,
      alert: { color: "info", message: "", isOpen: false },
      saving: false,
      quoteGenerating: false,
      modalClaimantSearchForm: false,
      modalClientSearchForm: false,
      modalRepSearchForm: false,
      modalInterpreterSearchForm: false,
      deltaPosition: { x: 0, y: 0 },
    };

    this.handleChange = this.handleChange.bind(this);
    this.onSave = this.onSave.bind(this);
    this.keydownHandler = this.keydownHandler.bind(this);
    this.onPrintQuote = this.onPrintQuote.bind(this);
    this.setModalClaimantSearchForm = this.setModalClaimantSearchForm.bind(this);
    this.handleSelectClaimant = this.handleSelectClaimant.bind(this);
    this.clearClaimant = this.clearClaimant.bind(this);
    this.setModalClientSearchForm = this.setModalClientSearchForm.bind(this);
    this.handleSelectClient = this.handleSelectClient.bind(this);
    this.setModalRepSearchForm = this.setModalRepSearchForm.bind(this);
    this.handleSelectRep = this.handleSelectRep.bind(this);
    this.setModalInterpreterSearchForm = this.setModalInterpreterSearchForm.bind(this);
    this.handleSelectInterpreter = this.handleSelectInterpreter.bind(this);
    this.handleBillingTotalChange = this.handleBillingTotalChange.bind(this);
  }

  // Job total follows the billing lines, as on the work order form; Save persists it.
  handleBillingTotalChange(total) {
    this.setState((prev) => ({ model: { ...prev.model, JobTotal: total } }));
  }

  componentDidMount() {
    document.addEventListener("keydown", this.keydownHandler);
    const { model } = this.state;
    // New translations start in the first workflow item.
    if (!model.WorkFlowId && this.props.workflow?.length) {
      this.setState((prev) => ({
        model: { ...prev.model, WorkFlowId: this.props.workflow[0] },
      }));
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.keydownHandler);
  }

  keydownHandler(e) {
    if (e.keyCode === 83 && e.altKey && !this.isLocked()) {
      this.onSave(this.state.model.EventType);
    }
  }

  isLocked() {
    return this.state.model.InvoicedFlg;
  }

  setModalClaimantSearchForm() {
    this.setState((prev) => ({ modalClaimantSearchForm: !prev.modalClaimantSearchForm }));
  }

  handleSelectClaimant(claimant) {
    const data = claimant.data;
    this.setState((prev) => ({
      model: {
        ...prev.model,
        ClmId: data.clmId,
        ClaimantDisplay: fullName(data.clmLastName, data.clmFirstName),
      },
      modalClaimantSearchForm: false,
    }));
  }

  clearClaimant() {
    this.setState((prev) => ({
      model: { ...prev.model, ClmId: null, ClaimantDisplay: "" },
    }));
  }

  setModalClientSearchForm() {
    this.setState((prev) => ({ modalClientSearchForm: !prev.modalClientSearchForm }));
  }

  handleSelectClient(client) {
    const data = client.data;
    this.setState((prev) => ({
      model: {
        ...prev.model,
        ClientId: data.clientId,
        ClientAbbrv: data.clientAbbrv,
        ClientDisplay: clientLabel(data.clientAbbrv, data.clientName),
        // A rep belongs to one client, so changing client clears the rep.
        ...(prev.model.ClientId === data.clientId ? {} : { RepId: "", RepDisplay: "" }),
      },
      modalClientSearchForm: false,
    }));
  }

  setModalRepSearchForm() {
    this.setState((prev) => ({ modalRepSearchForm: !prev.modalRepSearchForm }));
  }

  handleSelectRep(rep) {
    const data = rep.data;
    this.setState((prev) => ({
      model: {
        ...prev.model,
        RepId: data.repId,
        RepDisplay: fullName(data.repLastName, data.repFirstName),
      },
      modalRepSearchForm: false,
    }));
  }

  setModalInterpreterSearchForm() {
    this.setState((prev) => ({ modalInterpreterSearchForm: !prev.modalInterpreterSearchForm }));
  }

  handleSelectInterpreter(interpreter) {
    const data = interpreter.data;
    this.setState((prev) => ({
      model: {
        ...prev.model,
        IntId: data.intId,
        TranslatorDisplay: fullName(data.intLastName, data.intFirstName),
      },
      modalInterpreterSearchForm: false,
    }));
  }

  onPrintQuote() {
    const { model } = this.state;

    if (!model.JobId) {
      this.showAlert("Save the translation before printing a quote.", "warning");
      return;
    }
    if (!model.TargetLanguage) {
      this.showAlert("Target language is required.", "warning");
      return;
    }

    this.setState({ quoteGenerating: true });

    FetchFile(
      `/printtranslationquote?jobId=${model.JobId}`,
      "GET",
      this.user.token,
    ).then((blob) => {
      this.setState({ quoteGenerating: false });
      if (!blob || blob.size === 0) {
        this.showAlert("No file returned.", "warning");
        return;
      }
      openFileInNewTab(blob, "Translation Quote.pdf");
    });
  }

  showAlert(message, color) {
    this.setState({ alert: { color, message, isOpen: true } }, () => {
      window.setTimeout(() => {
        this.setState((prev) => ({ alert: { ...prev.alert, isOpen: false } }));
      }, Timeout(message));
    });
  }

  handleChange(e) {
    const { name, type, value, checked } = e.target;
    const model = { ...this.state.model };

    if (type === "checkbox") model[name] = checked;
    else if (type === "number") model[name] = value === "" ? null : parseFloat(value);
    else model[name] = value;

    this.setState({ model });
  }

  onSave(eventType) {
    const { model } = this.state;
    const payload = {
      ...model,
      EventType: eventType,
      FinishTime: model.FinishTime || null,
    };
    this.setState({ saving: true });

    FetchData("/savetranslation", "POST", this.user.token, payload).then(
      (data) => {
        this.setState({ saving: false });
        const message = data?.message ?? "Save failed.";

        if (message === "Success") {
          const verb = {
            insert: `Translation #${data.jobNum} created.`,
            update: `Translation #${data.jobNum} saved.`,
            delete: `Translation #${data.jobNum} deleted.`,
            undelete: `Translation #${data.jobNum} restored.`,
          }[eventType];

          if (eventType === "insert" || eventType === "update") {
            // Keep the form open on save; switch a freshly-created translation into
            // edit mode so a second save (or Print Quote) targets the new record.
            this.setState((prev) => ({
              model: {
                ...prev.model,
                EventType: "update",
                JobId: data.jobId,
                JobNum: data.jobNum,
              },
            }));
            this.showAlert(verb, "success");
            this.props.handleSaveEventSuccess({
              message: verb,
              color: "success",
              modal: true,
            });
          } else {
            this.props.handleSaveEventSuccess({
              message: verb,
              color: "success",
              modal: false,
            });
          }
        } else {
          this.showAlert(message, "warning");
        }
      },
    );
  }

  renderStatusPill() {
    const { model } = this.state;
    if (model.EventType === "insert") return <span className="tr-pill tr-pill-new">New</span>;
    if (model.IsDeletedFlg) return <span className="tr-pill tr-pill-deleted">Deleted</span>;
    if (model.InvoicedFlg) return <span className="tr-pill tr-pill-invoiced">Invoiced</span>;
    return <span className="tr-pill tr-pill-active">Active</span>;
  }

  render() {
    const { model, alert, deltaPosition, saving, quoteGenerating } = this.state;
    const isNew = model.EventType === "insert";
    const locked = this.isLocked();
    const readOnly = locked || model.IsDeletedFlg;
    const required = (
      <>
        <span className="tr-required" aria-hidden="true">*</span>
        <span className="tr-sr-only"> (required)</span>
      </>
    );

    return (
      <Draggable
        handle=".handle"
        onDrag={(e, ui) =>
          this.setState({
            deltaPosition: {
              x: deltaPosition.x + ui.deltaX,
              y: deltaPosition.y + ui.deltaY,
            },
          })
        }
      >
        <div>
          <Modal
            isOpen={this.props.modal}
            toggle={this.props.setModal}
            size="xl"
            modalClassName="tr-modal"
            style={{
              transform: `translate(${deltaPosition.x}px, ${deltaPosition.y}px)`,
            }}
            backdrop="static"
          >
            <ModalHeader className="handle" toggle={this.props.setModal}>
              <span className="tr-modal-title">
                {isNew ? "New translation" : `Translation #${model.JobNum}`}
                {model.WorkFlowId && (
                  <span className="tr-pill tr-pill-workflow">{model.WorkFlowId}</span>
                )}
                {this.renderStatusPill()}
              </span>
            </ModalHeader>
            <ModalBody>
              <Alert color={alert.color} isOpen={alert.isOpen} role="alert">
                {alert.message}
              </Alert>
              {locked && (
                <Alert color="info">This translation has been invoiced and can no longer be changed.</Alert>
              )}
              {model.IsDeletedFlg && (
                <Alert color="secondary">This translation is deleted. Restore it to make changes.</Alert>
              )}

              {/* Left column follows the printed "Translation Estimate and Agreement" top to
                  bottom; the right column holds job details that don't print on the quote. */}
              <div className="row g-3">
                <div className="col-lg-7">
                  <fieldset disabled={readOnly}>
                    <section className="tr-section" aria-label="Estimate and agreement">
                      <div className="row g-2">
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-date">
                            Date{required}
                          </label>
                          <input
                            id="tr-date"
                            type="date"
                            name="JobDateTime"
                            className="form-control"
                            value={model.JobDateTime}
                            onChange={this.handleChange}
                            aria-required="true"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-workflow">
                            Workflow
                          </label>
                          <select
                            id="tr-workflow"
                            name="WorkFlowId"
                            className="form-select"
                            value={model.WorkFlowId}
                            onChange={this.handleChange}
                          >
                            {(this.props.workflow ?? []).map((item) => (
                              <option key={item} value={item}>
                                {item}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-client-search">
                            Client{required}
                          </label>
                          {model.ClientDisplay ? (
                            <div className="tr-selected-chip">
                              <span>{model.ClientDisplay}</span>
                              {!readOnly && (
                                <Button size="sm" color="link" onClick={this.setModalClientSearchForm}>
                                  Change
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Button
                              id="tr-client-search"
                              size="sm"
                              color="secondary"
                              outline
                              onClick={this.setModalClientSearchForm}
                            >
                              Search client
                            </Button>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-rep-search">
                            Attn (rep){required}
                          </label>
                          {model.RepDisplay ? (
                            <div className="tr-selected-chip">
                              <span>{model.RepDisplay}</span>
                              {!readOnly && (
                                <Button size="sm" color="link" onClick={this.setModalRepSearchForm}>
                                  Change
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Button
                              id="tr-rep-search"
                              size="sm"
                              color="secondary"
                              outline
                              disabled={!model.ClientId}
                              onClick={this.setModalRepSearchForm}
                              title={model.ClientId ? "" : "Choose a client first"}
                            >
                              Search rep
                            </Button>
                          )}
                        </div>

                        <div className="col-12">
                          <div className="tr-lang-box row g-1">
                            <div className="col-md-6">
                              <label className="form-label" htmlFor="tr-quote-source-language">
                                Source language{required}
                              </label>
                              <input id="tr-quote-source-language" type="text" name="SourceLanguage" className="form-control" value={model.SourceLanguage} onChange={this.handleChange} />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label" htmlFor="tr-quote-target-language">
                                Target language(s){required}
                              </label>
                              <input id="tr-quote-target-language" type="text" name="TargetLanguage" className="form-control" value={model.TargetLanguage} onChange={this.handleChange} />
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <label className="form-label" htmlFor="tr-job-notes">
                            Subject
                          </label>
                          <textarea
                            id="tr-job-notes"
                            name="InvoiceNote"
                            rows={3}
                            className="form-control"
                            value={model.InvoiceNote}
                            onChange={this.handleChange}
                            placeholder="One document per line, e.g. 5821 Consent 11-20-24 (4)"
                          />
                        </div>

                      </div>
                    </section>
                  </fieldset>
                </div>

                <div className="col-lg-5">
                  <fieldset disabled={readOnly}>
                    <section className="tr-section" aria-label="Translator and claimant">
                      <div className="row g-2">
                        <div className="col-12">
                          <label className="form-label" htmlFor="tr-translator-search">
                            Translator{required}
                          </label>
                          {model.TranslatorDisplay ? (
                            <div className="tr-selected-chip">
                              <span>{model.TranslatorDisplay}</span>
                              {!readOnly && (
                                <Button size="sm" color="link" onClick={this.setModalInterpreterSearchForm}>
                                  Change
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Button
                              id="tr-translator-search"
                              size="sm"
                              color="secondary"
                              outline
                              onClick={this.setModalInterpreterSearchForm}
                            >
                              Search translator
                            </Button>
                          )}
                        </div>
                        <div className="col-12">
                          <label className="form-label" htmlFor="tr-claimant-search">
                            Claimant <span className="tr-hint">(optional)</span>
                          </label>
                          {model.ClaimantDisplay ? (
                            <div className="tr-selected-chip">
                              <span>{model.ClaimantDisplay}</span>
                              {!readOnly && (
                                <>
                                  <Button size="sm" color="link" onClick={this.setModalClaimantSearchForm}>
                                    Change
                                  </Button>
                                  <Button size="sm" color="link" className="text-danger" onClick={this.clearClaimant}>
                                    Remove
                                  </Button>
                                </>
                              )}
                            </div>
                          ) : (
                            <Button
                              id="tr-claimant-search"
                              size="sm"
                              color="secondary"
                              outline
                              onClick={this.setModalClaimantSearchForm}
                            >
                              Search claimant
                            </Button>
                          )}
                        </div>
                        <div className="col-12">
                          <label className="form-label" htmlFor="tr-translator-notes">
                            Notes
                          </label>
                          <textarea
                            id="tr-translator-notes"
                            name="IntApptNotes"
                            rows={2}
                            className="form-control"
                            value={model.IntApptNotes}
                            onChange={this.handleChange}
                          />
                        </div>
                      </div>
                    </section>

                    <section className="tr-section" aria-label="Due date and fees">
                      <div className="row g-2">
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-due-date">
                            Due Date <span className="tr-hint">(optional)</span>
                          </label>
                          <input
                            id="tr-due-date"
                            type="date"
                            name="FinishTime"
                            className="form-control"
                            value={model.FinishTime}
                            onChange={this.handleChange}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-turnaround">Turnaround (days)</label>
                          <input id="tr-turnaround" type="number" min="0" name="TurnaroundDays" className="form-control" value={model.TurnaroundDays ?? ""} onChange={this.handleChange} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-fee">
                            Translator fee
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              id="tr-fee"
                              type="number"
                              step="0.01"
                              min="0"
                              name="IntFee"
                              className="form-control"
                              value={model.IntFee ?? ""}
                              onChange={this.handleChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="tr-total">
                            Job total
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              id="tr-total"
                              type="number"
                              step="0.01"
                              min="0"
                              name="JobTotal"
                              className="form-control"
                              value={model.JobTotal ?? ""}
                              onChange={this.handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </section>
                  </fieldset>
                </div>
              </div>

              {!isNew && (
                <TranslationBilling
                  jobId={model.JobId}
                  language={model.TargetLanguage}
                  readOnly={readOnly}
                  onTotalChange={this.handleBillingTotalChange}
                  onMessage={(message) => this.showAlert(message, "info")}
                />
              )}

              {/* Full width at the bottom, like the work order's job history. Outside the
                  fieldsets: like work orders, invoiced jobs can still get notes. */}
              {isNew ? (
                <section className="tr-section">
                  <h3 className="tr-section-title">Translation History</h3>
                  <p className="tr-hint">Create the translation first, then add notes and attachments here.</p>
                </section>
              ) : (
                <TranslationHistory
                  jobId={model.JobId}
                  readOnly={model.IsDeletedFlg}
                  onMessage={(message) => this.showAlert(message, "info")}
                />
              )}
            </ModalBody>
            <ModalFooter>
              {!isNew && !locked && !model.IsDeletedFlg && (
                <Button
                  color="danger"
                  outline
                  className="me-auto"
                  disabled={saving}
                  onClick={() => {
                    if (window.confirm(`Delete Translation #${model.JobNum}?`)) {
                      this.onSave("delete");
                    }
                  }}
                >
                  Delete
                </Button>
              )}
              {!isNew && !locked && model.IsDeletedFlg && (
                <Button
                  color="warning"
                  className="me-auto"
                  disabled={saving}
                  onClick={() => this.onSave("undelete")}
                >
                  Restore
                </Button>
              )}
              {!isNew && (
                <Button color="info" outline disabled={quoteGenerating} onClick={this.onPrintQuote}>
                  {quoteGenerating ? "Generating…" : "Print Quote"}
                </Button>
              )}
              <Button color="secondary" outline onClick={this.props.setModal} disabled={saving}>
                {readOnly ? "Close" : "Cancel"}
              </Button>
              {!readOnly && (
                <Button
                  color="primary"
                  disabled={saving}
                  onClick={() => this.onSave(model.EventType)}
                  title="Alt+S"
                >
                  {saving ? "Saving…" : isNew ? "Create translation" : "Save"}
                </Button>
              )}
            </ModalFooter>
          </Modal>
          {this.state.modalClaimantSearchForm && (
            <ClaimantFormSearch
              modal={this.state.modalClaimantSearchForm}
              alert={alert}
              setModal={this.setModalClaimantSearchForm}
              Name=""
              workorder={model}
              handleDoubleClick={this.handleSelectClaimant}
              setFetchingData={() => {}}
            />
          )}
          {this.state.modalClientSearchForm && (
            <ClientFormSearch
              modal={this.state.modalClientSearchForm}
              setModal={this.setModalClientSearchForm}
              handleDoubleClick={this.handleSelectClient}
              setFetchingData={() => {}}
            />
          )}
          {this.state.modalRepSearchForm && (
            <RepFormSearch
              modal={this.state.modalRepSearchForm}
              setModal={this.setModalRepSearchForm}
              ClientAbbrv={model.ClientAbbrv}
              handleDoubleClick={this.handleSelectRep}
              setFetchingData={() => {}}
            />
          )}
          {this.state.modalInterpreterSearchForm && (
            <InterpreterFormSearch
              modal={this.state.modalInterpreterSearchForm}
              alert={alert}
              setModal={this.setModalInterpreterSearchForm}
              Name=""
              handleDoubleClick={this.handleSelectInterpreter}
              setFetchingData={() => {}}
            />
          )}
        </div>
      </Draggable>
    );
  }
}
